import { Input, Select, TextArea } from '../../components/UI';
import type { PaymentRecord } from '../../types';
import { formatCurrency } from '../../utils/formatters';
import type { InvoiceEditorFinancialSummary } from '../../utils/invoiceFinancials';

export interface CreationDepositDraft {
  amount: number;
  date: string;
  method: PaymentRecord['method'];
  reference: string;
  notes: string;
}

export const createEmptyCreationDeposit = (): CreationDepositDraft => ({
  amount: 0,
  date: new Date().toISOString().split('T')[0],
  method: 'bank_transfer',
  reference: '',
  notes: '',
});

interface InvoiceEditorSummaryProps {
  currency?: 'AUD' | 'USD';
  financials: InvoiceEditorFinancialSummary;
  isNewInvoice: boolean;
  creationDeposit: CreationDepositDraft;
  onCreationDepositChange: (next: CreationDepositDraft) => void;
}

export const InvoiceEditorSummary = ({
  currency,
  financials,
  isNewInvoice,
  creationDeposit,
  onCreationDepositChange,
}: InvoiceEditorSummaryProps) => (
  <div className="space-y-4">
    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
      <h5 className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-4">Summary</h5>
      <div className="space-y-2">
        <div className="flex justify-between text-sm"><span className="text-slate-500 font-medium">Subtotal</span><span className="font-semibold">{formatCurrency(financials.subtotal, currency)}</span></div>
        <div className="flex justify-between text-sm"><span className="text-slate-500 font-medium">Tax</span><span className="font-semibold">{formatCurrency(financials.taxAmount, currency)}</span></div>
        <div className="h-px bg-slate-200 my-2" />
        <div className="flex justify-between text-xl font-semibold"><span className="text-slate-900">Total</span><span className="text-teal-600">{formatCurrency(financials.total, currency)}</span></div>
        <div className="flex justify-between text-sm"><span className="text-slate-500 font-medium">Amount Paid</span><span className="font-semibold text-emerald-600">{formatCurrency(financials.amountPaid, currency)}</span></div>
        <div className="flex justify-between text-xl font-semibold"><span className="text-slate-900">Balance Due</span><span className="text-amber-600">{formatCurrency(financials.balanceDue, currency)}</span></div>
      </div>
    </div>

    {isNewInvoice && (
      <div className="bg-white rounded-xl p-4 border border-slate-200 space-y-3">
        <div>
          <h5 className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Deposit already received (optional)</h5>
          <p className="text-[11px] text-slate-500 mt-1">If entered, the invoice is created first and the deposit is then stored as a real payment against that invoice.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Deposit Amount" type="number" min="0" step="0.01" value={creationDeposit.amount || ''} onChange={(e: any)=> onCreationDepositChange({...creationDeposit, amount: Number(e.target.value) || 0})} />
          <Input label="Payment Date" type="date" value={creationDeposit.date} onChange={(e: any)=> onCreationDepositChange({...creationDeposit, date: e.target.value})} />
          <Select label="Method" value={creationDeposit.method} options={[{label: 'Bank Transfer',value: 'bank_transfer'},{label: 'Card',value: 'card'},{label: 'Cash',value: 'cash'},{label: 'PayPal',value: 'paypal'},{label: 'Stripe',value: 'stripe'},{label: 'Other',value: 'other'}]} onChange={(e: any)=> onCreationDepositChange({...creationDeposit, method: e.target.value as PaymentRecord['method']})} />
          <Input label="Reference" value={creationDeposit.reference} onChange={(e: any)=> onCreationDepositChange({...creationDeposit, reference: e.target.value})} />
        </div>
        <TextArea label="Deposit Notes" value={creationDeposit.notes} onChange={(e: any)=> onCreationDepositChange({...creationDeposit, notes: e.target.value})} rows={2} />
        <p className="text-[10px] text-slate-400">No separate payment receipt is automatically emailed during invoice creation. The invoice itself will show the paid amount and remaining balance.</p>
      </div>
    )}
  </div>
);